import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Play, Sparkles, Pencil, ArrowRight, ChevronDown, Network, MessageCircle, Github, Lightbulb, Users } from 'lucide-react'
import Spinner from './Spinner'
import SubscriptionGate from './SubscriptionGate'
import type { AppDispatch, RootState } from '../store'
import type { Project, GithubSettings } from '../data/projectTypes'
import {
  useCreateProjectMutation,
  useDeleteReverseProxyMutation,
  useDeleteProjectMutation,
  useGetGithubAppInstallationQuery,
  useGetReverseProxyStatusQuery,
  useStartProjectQaMutation,
  useUpdateUserSettingsMutation,
  useUpdateProjectMutation,
} from '../services/projectDataApi'
import ReverseProxyChatCard from './chat/ReverseProxyChatCard'
import GithubConnectChatCard from './chat/GithubConnectChatCard'
import { buildGithubSettings, type GithubTestingAnswers, DEFAULT_GITHUB_TESTING_ANSWERS } from '../lib/githubTesting'
import {
  type BugDestinationAnswer,
  destinationUsesGithubIssues,
  destinationIssueTracker,
  type IssueTrackerCreatePayload,
} from '../lib/bugDestination'
import { useNavigate } from './nav'
import { getNewProjectTeamId, useProjectTeam } from '../hooks/useProjectTeam'
import { login } from './UserMenu'
import { detectWebsiteFeatures, normalizeUrl, type WebsiteFeatures } from '../lib/detectWebsiteFeatures'
import {
  buildLoginGlobalVariables,
  buildScopeExplorationPrompt,
  resolveLoginMode,
  loginModeGlobalVariable,
  type LoginChoice,
} from './qaConfig'
import ChatMessageList from './chat/ChatMessageList'
import ChatInput from './chat/ChatInput'
import ChatLoginsForm from './chat/ChatLoginsForm'
import ChatChoices from './chat/ChatChoices'
import { AgentCta } from './OnboardingCallouts'
import { createMessage, mergeMessage, toWireMessages, type ChatMessageData } from './chat/chatModel'
import { capTranscript } from '../../netlify/functions/lib/project-chat-transcript'
import { chatMessagesForSeed } from './chat/projectChatHistory'
import { ONBOARDING_COPY } from './chat/onboardingCopy'
import { streamChat } from './chat/postChat'
import { requestSidebarExpand } from '../lib/sidebarChat'
import { trackEvent } from '../lib/analytics'
import { PENDING_CHAT_PROJECT_KEY } from '../lib/pendingChatProject'
import { saveGithubSetupParams, takeGithubSetupParams, takeGithubSetupReturnPath } from '../lib/githubSetupParams'
import { reportChatFailure, reportChatStall, chatFailureNotice, type ChatTurnContext } from './chat/chatTelemetry'
import { DEFAULT_ONBOARDING_CONFIG, loginChoiceSet, securityChoiceSet, type ChatChoice, type ChatChoiceSet, type ChatFormRequest, type OnboardingConfig, type OnboardingCopy } from '../../netlify/functions/lib/project-chat-types'
import SecurityNonceChatCard, { type SecurityNonceState } from './chat/SecurityNonceChatCard'
import { environmentNameForKind, inferProjectEnvironmentKind } from '../lib/projectEnvironment'
import { currentChatTimezone } from '../lib/chatTimezone'
import './chat/chat.css'
import './OnboardingChat.css'

// The chat-based onboarding surface (shown on the Dashboard). The user enters ONLY an app URL; we
// detect its features (detectWebsiteFeatures). If it's behind a login we collect the user's choice in
// a short step; if it's a localhost / private-network URL we narrate that a reverse proxy is needed
// (set up inline via ReverseProxyChatCard, which gates QA from starting). Once the app is squared away
// we ask how this environment should run: GitHub deployments, a nightly/weekly schedule, or once.
// Connecting walks the GitHub App flow; scheduling records the cadence on the environment; run-once
// goes straight to Start QA. Then the
// chat shows the sensible default with a Start QA button — the user can start, or send messages to
// adjust anything first. The chat client model + components are adapted from replayio/nut.new.

// `checking` marks the opening "Checking <url>…" assistant message shown while the initial app probe
// runs. It renders like any other chat message (it's the first message in the transcript); once
// detection completes the flag is cleared and the finding (reachable / needs-login / needs-proxy) is
// appended after it (see finalizeChecking).
type UIMessage = ChatMessageData & { hidden?: boolean; checking?: boolean }

// url → chat, with an optional login step. Entering a website URL drops straight into the chat shell
// and runs the reachability/login probe *inside* it (the `detecting` flag). The login step only
// appears when detection says the site is behind a login. After the app is ready, the GitHub-offer
// sub-flow runs inside the chat (see GithubStage) before Start QA is surfaced.
//
// The GitHub-first branch has no separate phase: when the user enters a GitHub repository URL as the
// initial URL we don't reject it — we capture the repo and open the chat right away, asking for the
// deployed app URL there (the `awaitingAppUrl` flag) rather than on a look-alike landing page. Once
// they answer, the normal flow resumes (detection → optional login → chat). Because the repo is
// already known, the GitHub sub-flow skips its Connect/Skip offer and connects that repo directly.
type Phase = 'url' | 'chat'

// The run-mode/GitHub sub-flow that runs (inside the chat phase) after the app is ready:
//   idle        — nothing yet: the reachability check hasn't confirmed the site is testable, so no
//                 GitHub options / composer / Start QA are shown; for local apps it also means the
//                 local computer connection is still being established.
//   offer       — GitHub / schedule / run-once choices.
//   schedule    — nightly / weekly choices.
//   repo        — the user entered Connect: collect the repository URL.
//   connect     — GithubConnectChatCard (install / add the App, disconnect any existing project). Once
//                 connected we go straight to done: main-branch testing is auto-set to detect
//                 deployments to the app URL, and the "where should bugs go?" step is disabled for now.
//   done        — GitHub fully configured → seed the intro + Start QA.
//   skipped     — user skipped GitHub → seed the intro + Start QA.
type GithubStage = 'idle' | 'offer' | 'schedule' | 'repo' | 'connect' | 'done' | 'skipped'

// The opening "Checking <url>…" assistant message, seeded the instant the flow enters the chat shell
// so the chat is never empty. It's an ordinary message — the first one in the transcript; runDetection
// appends the finding after it (see finalizeChecking).
function makeCheckingMessage(content: string): UIMessage {
  return { ...createMessage('assistant', content), checking: true }
}

function urlLooksValid(raw: string): boolean {
  if (!raw.trim()) return false
  try {
    const u = new URL(normalizeUrl(raw))
    return u.hostname.includes('.') || u.hostname === 'localhost'
  } catch {
    return false
  }
}

// The landing composer is a prompt box, so a paste is often a launch sentence — "https://app.example
// — a small task app. Set up QA for it." — not a bare URL. Everything after the leading URL is prose
// for the person reading it; carried into target_url it reaches POST /api/projects, whose validation
// rejects it with 400 "Invalid target_url", dead-ending setup at "Failed to prepare the local
// computer connection". Extract the leading URL token and leave the prose behind. With no URL-shaped
// token the input comes back unchanged, so the urlLooksValid gate refuses it exactly as before.
function leadingUrl(raw: string): string {
  const token = raw.match(/https?:\/\/\S+|localhost(?::\d+)?(?:[/?#]\S*)?|[a-z0-9-]+(?:\.[a-z0-9-]+)+(?::\d+)?(?:[/?#]\S*)?/i)?.[0] ?? raw
  // A raw em-dash cannot appear in a valid URL (it must be percent-encoded), so prose separators like
  // "app — description" split there; trailing sentence punctuation does too.
  return (token.split(/[—–]/)[0]?.replace(/[.,;:!?)\]}>'"…]+$/, '')) || raw
}

function isGithubUrl(raw: string): boolean {
  if (!raw.trim()) return false
  try {
    const u = new URL(normalizeUrl(raw))
    return u.hostname === 'github.com' || u.hostname === 'www.github.com'
  } catch {
    return false
  }
}

// owner/repo label for a GitHub repository URL, for display (falls back to the raw URL).
function githubRepoLabel(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/^\/+|\/+$/g, '')
    return path || url
  } catch {
    return url
  }
}

function deriveProjectName(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url || 'New Project'
  }
}

// Flatten the chat-collected config into the createProject payload — mirrors StartQaForm.submitProject,
// reusing the same qaConfig helpers so a chat-created project is identical to a form-created one.
// The login answer a submitted logins form represents. Submitting with no credentials is the form's
// "let QA sign up for its own account" path (ChatLoginsForm always reports autoCreateAccount=true), so
// it is an explicit self-signup answer — not an unanswered login question.
function loginChoiceFor(loginCount: number): LoginChoice {
  return loginCount > 0 ? 'provided' : 'self-signup'
}

function buildCreatePayload(targetUrl: string, config: OnboardingConfig) {
  // Last stop before POST /api/projects. Whatever path let a combined "URL — description" string into
  // targetUrl (a draft resumed from sessionStorage across a deploy, a future capture path), the create
  // body must carry a plain URL: the server rejects anything else with 400 "Invalid target_url", and
  // setup dead-ends at "Failed to prepare the local computer connection".
  const url = leadingUrl(targetUrl)
  const globalVars = buildLoginGlobalVariables(config.logins)
  globalVars.push(loginModeGlobalVariable(resolveLoginMode({
    logins: config.logins,
    autoCreateAccount: config.autoCreateAccount,
    isChatOnboarding: true,
    loginChoice: config.loginChoice,
  })))
  const hasDesignDoc = !!config.designDocument.trim()
  const focusedScope = config.scopeMode === 'focus' && (config.scopeAreas.length > 0 || !!config.instructions.trim())
  const explorationPrompt = hasDesignDoc && !focusedScope
    ? undefined
    : buildScopeExplorationPrompt({
        scopeMode: config.scopeMode,
        scopeAreas: config.scopeAreas,
        instructions: config.instructions,
        autoCreateAccount: config.autoCreateAccount,
        logins: config.logins,
      })
  const environmentKind = config.environmentKind ?? inferProjectEnvironmentKind(url)
  const triggerConfig = config.environmentTrigger === 'schedule'
    ? {
        cadence: config.environmentSchedule,
        timezone: currentChatTimezone(),
      }
    : {}
  const primaryEnvironment = {
    name: environmentNameForKind(environmentKind),
    kind: environmentKind,
    target_url: url,
    trigger_type: config.environmentTrigger,
    trigger_config: triggerConfig,
  }
  return {
    name: deriveProjectName(url),
    target_url: url,
    global_variables: globalVars.length > 0 ? globalVars : undefined,
    design_document: hasDesignDoc ? config.designDocument.trim() : undefined,
    instructions: explorationPrompt,
    // Persistent testing instructions the user gave in the onboarding chat (things to focus on, and
    // especially limits like "don't change my data"). Unlike `instructions`, these are stored on the
    // project and applied to the first exploration, to journey discovery, and to later version/PR tests.
    testing_guidelines: config.testingGuidelines.trim() || undefined,
    enabled_polish_passes: [...config.enabledPolishPasses],
    security_probing_enabled: config.securityProbingEnabled || undefined,
    security_nonce: config.securityNonce || undefined,
    security_ownership_exempt: config.securityOwnershipExempt || undefined,
    use_reverse_proxy: config.useReverseProxy || undefined,
    budget: config.budget,
    // Which team owns (and pays for) the project — the answer to the "Select team" picker below. Read
    // from the module store rather than passed in, because this builder is called from four places
    // including a signed-out resume path. Undefined when the user never picked, and the server then
    // uses their own team, which is also what the picker shows as the default; it re-checks membership
    // either way, so a stale id can only ever fall back — never spend another team's credits.
    team_id: getNewProjectTeamId() ?? undefined,
    environments: config.environmentTrigger === 'github-app' && environmentKind !== 'preview'
      ? [primaryEnvironment, {
          name: 'Preview',
          kind: 'preview' as const,
          target_url: null,
          trigger_type: 'github-app' as const,
          trigger_config: {},
        }]
      : [primaryEnvironment],
  }
}

// The persisted GitHub sub-flow state, carried across a sign-in round-trip so the walkthrough resumes
// where it left off. `stage` restores the sub-flow; `start` (on the parent) means the user pressed
// Start QA while signed out, so the resume creates the project.
type PendingGithub = {
  stage: GithubStage
  repoUrl?: string
  repo?: { repoFullName: string; defaultBranch: string }
  options?: GithubTestingAnswers
  destination?: BugDestinationAnswer | null
}
// `reverseProxy` marks a pending project that must resume into the paused reverse-proxy flow (recreate
// the paused project + stay on onboarding). `github` carries the sub-flow state. `start` means the
// user pressed Start QA signed out (non-proxy) → create + navigate on resume.
type PendingChatProject = {
  targetUrl: string
  config: OnboardingConfig
  messages?: UIMessage[]
  reverseProxy?: boolean
  github?: PendingGithub
  start?: boolean
}

// Interpolate {url} / {repo} tokens in a copy string from src/guidance/chat/onboarding.json. Tolerates a
// missing template (undefined) — an absent copy field renders empty
// rather than crashing the page — so callers don't each need to guard against it.
function fill(template: string | undefined, vars: Record<string, string>): string {
  return (template ?? '').replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '')
}

// The Connect / Skip choices under the GitHub-offer message, built from the onboarding copy.
function githubOfferChoiceSet(copy: OnboardingCopy): ChatChoiceSet {
  return {
    prompt: copy.githubOfferPrompt,
    choices: [
      { id: 'connect', label: copy.githubConnectLabel, description: copy.githubConnectDescription, action: { kind: 'message', message: 'connect' } },
      { id: 'schedule', label: copy.scheduleLabel, description: copy.scheduleDescription, action: { kind: 'message', message: 'schedule' } },
      { id: 'once', label: copy.runOnceLabel, description: copy.runOnceDescription, action: { kind: 'message', message: 'once' } },
    ],
  }
}

function scheduleChoiceSet(copy: OnboardingCopy): ChatChoiceSet {
  return {
    prompt: copy.schedulePrompt,
    choices: [
      { id: 'nightly', label: copy.nightlyLabel, description: copy.nightlyDescription, action: { kind: 'message', message: 'nightly' } },
      { id: 'weekly', label: copy.weeklyLabel, description: copy.weeklyDescription, action: { kind: 'message', message: 'weekly' } },
    ],
  }
}

export default function OnboardingChat() {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const [createProject] = useCreateProjectMutation()
  const [deleteReverseProxy] = useDeleteReverseProxyMutation()
  const [startQa] = useStartProjectQaMutation()
  const [deleteProject] = useDeleteProjectMutation()
  const [updateUserSettings] = useUpdateUserSettingsMutation()
  const [updateProject] = useUpdateProjectMutation()
  const { user, checked } = useSelector((state: RootState) => state.auth)
  const { teams, team: projectTeam, setTeam: setProjectTeam } = useProjectTeam({ skip: !user })
  // Whether the team this project would be created in can actually pay for the run. The surface-level
  // gate (RequireCredits) asks a different question — "can this person run anything anywhere" — and
  // answers yes for someone whose own team is empty but who belongs to a funded one. Creating in the
  // empty team would then start QA that can't be paid for, so the choice is re-checked here against
  // the team that was actually picked. Only a resolved balance of zero refuses: no team yet (still
  // loading, or a billing lookup that failed) leaves starting alone, since the server reports a
  // generous balance whenever it cannot be sure.
  const teamCannotPay = !!user && !!projectTeam && projectTeam.available_credits <= 0
  const [showGate, setShowGate] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const urlParam = searchParams.get('url') ?? ''
  // Set when GitHub's App "Setup URL" landed the user here after an install — see the post-install
  // landing effects below.
  const installationIdParam = searchParams.get('installation_id') ?? ''

  // A ?url= deep-link bypasses submitUrl, so apply the same GitHub-repo handling here: a repo URL is
  // reachable (github.com) and would otherwise sail through detection as if it were the app. Instead
  // of rejecting it, we capture the repo and open the chat asking for the deployed app URL.
  const urlParamIsGithub = isGithubUrl(urlParam)
  const initialValid = urlLooksValid(urlParam) && !urlParamIsGithub
  // Setup is account work: it creates a project, spends a team's credits, and parks a conversation on
  // the server. So signing in is the entrance, not a step near the end — a signed-out visitor gets the
  // landing (which doubles as the product pitch) and nothing past it.
  //
  // That has to include the ?url= deep-link, which otherwise opens the flow without ever passing the
  // button. Signed out it lands on the landing with the URL prefilled instead, so the one click there
  // carries it through sign-in and resumes on the app they asked for.
  //
  // Reading `user` during the first render is safe because RequireCredits holds this whole surface
  // behind a loading pane until the session is resolved: there is no frame where a signed-in user
  // looks like a visitor, so nobody gets bounced to a login they did not need.
  const deepLinkReady = !!user && (initialValid || urlParamIsGithub)
  const [phase, setPhase] = useState<Phase>(deepLinkReady ? 'chat' : 'url')
  const [targetUrl, setTargetUrl] = useState(user && initialValid ? normalizeUrl(leadingUrl(urlParam)) : '')
  // Signed out, whatever was deep-linked stays in the box — including a repo URL, which the signed-in
  // path hides because the chat collects the app URL instead. Losing it across sign-in would leave the
  // user retyping the thing they arrived with.
  const [urlDraft, setUrlDraft] = useState(user && urlParamIsGithub ? '' : urlParam)
  // The "Checking…" message is seeded once copy is available — by submitUrl for a typed URL, or by
  // runDetection for a ?url= deep-link (its text comes from the onboarding copy entry).
  const [messages, setMessages] = useState<UIMessage[]>([])
  const [config, setConfig] = useState<OnboardingConfig>({ ...DEFAULT_ONBOARDING_CONFIG })
  const [pending, setPending] = useState(false)
  // What the agent is doing right now ("Reading that link…"), streamed as `status` events while a tool
  // call is in flight. The agent also routes its own tool-call announcement here rather than into the
  // reply, so with nothing listening those lines would simply vanish.
  const [agentStatus, setAgentStatus] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  // Once setup is ready (all steps done) the composer is hidden behind a subtle "Need to tell me
  // something else?" affordance so the prominent action is Start QA, not a blinking input.
  const [composerRevealed, setComposerRevealed] = useState(false)
  // True while the initial reachability/login probe runs — the chat shell shows the "Checking…"
  // message until the finding is appended and the GitHub offer takes over.
  const [detecting, setDetecting] = useState(deepLinkReady && initialValid)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Reverse-proxy flow: the paused project created up-front so its tunnel can connect before QA
  // starts, and a spinner flag while that create is in flight. `proxyReady` reflects the detected
  // tunnel connection (populated by ReverseProxyChatCard's polling) — it gates the Start QA button.
  const [proxyProject, setProxyProject] = useState<Project | null>(null)
  const [gating, setGating] = useState(false)
  const reverseProxyStatusQuery = useGetReverseProxyStatusQuery(proxyProject?.id ?? '', {
    skip: !proxyProject,
    pollingInterval: 5_000,
    skipPollingIfUnfocused: true,
  })
  const proxyReady = reverseProxyStatusQuery.data?.ready === true
  // GitHub-integration sub-flow (idle → offer → repo → connect → questions → destination → done|skipped).
  // Starts 'idle' — nothing is shown until the reachability check confirms the site is testable and
  // moves it to 'offer' (see proceedToGithubOffer). Answers are folded into the create payload at Start QA.
  const [githubStage, setGithubStage] = useState<GithubStage>('idle')
  // Seeded with the repository URL when the user starts from a GitHub URL (typed or ?url= deep-link);
  // otherwise collected in the GitHub sub-flow's `repo` stage.
  const [repoUrl, setRepoUrl] = useState(user && urlParamIsGithub ? normalizeUrl(urlParam) : '')
  // GitHub-first: true while the chat is asking for the deployed app URL (before detection runs). The
  // question is a normal assistant message; the answer comes from a composer in the chat body.
  const [awaitingAppUrl, setAwaitingAppUrl] = useState(!!user && urlParamIsGithub)
  const [githubRepo, setGithubRepo] = useState<{ repoFullName: string; defaultBranch: string } | null>(null)
  const [githubOptions, setGithubOptions] = useState<GithubTestingAnswers>(DEFAULT_GITHUB_TESTING_ANSWERS)
  // The "where should bugs go?" step is disabled for now (untested), so this stays null — no external
  // bug destination is configured from onboarding. The plumbing below is kept for when it's re-enabled.
  const [bugDestination] = useState<BugDestinationAnswer | null>(null)
  const [pendingForm, setPendingForm] = useState<ChatFormRequest | null>(null)
  // Clickable choices the agent offered this turn (via offer_choices) — rendered as buttons below the
  // transcript until the user picks one.
  const [pendingChoices, setPendingChoices] = useState<ChatChoiceSet | null>(null)
  // On the login question, whether the user picked "Provide login credentials" — which reveals the
  // add-logins form inline instead of the choice buttons.
  const [loginStepForm, setLoginStepForm] = useState(false)
  // Whether the login question is being asked inline in the transcript (below the finding message),
  // rendered like every other in-chat prompt (e.g. the GitHub offer) — never a full-screen step. Set
  // for BOTH a confirmed login wall and an uncertain (SPA-shell) result; cleared once answered.
  const [awaitingLoginChoice, setAwaitingLoginChoice] = useState(false)
  // All onboarding copy, from the `onboarding` guidance JSON entry (the sole source — no in-code copy).
  // Bundled at build time, so it is available on the first render: no fetch, no loading gate, and no
  // way for this surface to strand the user before it can show anything.
  const copy = ONBOARDING_COPY
  // True once "Change URL" has been clicked and the header is asking to confirm the reset.
  const [confirmReset, setConfirmReset] = useState(false)
  // Set when one of the sign-in flows below (Start QA / reverse proxy / GitHub connect) has already
  // written a pending snapshot carrying its own resume flags, so the passive save must not overwrite it.
  const explicitPersist = useRef(false)
  // A single randomized onboarding tip (picked once, so it doesn't reshuffle on re-render).
  const [tip] = useState<string | null>(() => {
    const tips = copy.tips ?? []
    return tips.length > 0 ? tips[Math.floor(Math.random() * tips.length)] ?? null : null
  })
  const kickedOff = useRef(false)
  const detectStarted = useRef(false)
  // True when the user started from a GitHub repository URL: the app URL is collected in the chat
  // (awaitingAppUrl) and the GitHub sub-flow skips its Connect/Skip offer, connecting the repo directly.
  const startedFromGithub = useRef(urlParamIsGithub)
  // Guards the up-front paused-project creation for the reverse-proxy flow so it runs exactly once.
  const gatedCreated = useRef(false)
  // Guards the one-time GitHub-offer narration.
  const offerSeeded = useRef(false)
  // Security-pass sub-flow, asked after GitHub and before the ready intro:
  //   idle    — not reached yet (or suppressed: a reverse-proxied app can't be checked from outside).
  //   offer   — the yes/no question is on screen and blocks Start QA, like the GitHub offer.
  //   setup   — they said yes and the ownership card is up. Deliberately does NOT block: publishing a
  //             file usually needs a deploy, so the intro is seeded at this point and QA can start
  //             without it. The pass stays inert until the nonce verifies (the runner re-checks anyway).
  //   done / skipped — resolved.
  const [securityStage, setSecurityStage] = useState<'idle' | 'offer' | 'setup' | 'done' | 'skipped'>('idle')
  // Which ready-intro to seed once the security step lets go — the GitHub flow decided that before the
  // question was asked, so it has to be remembered rather than recomputed.
  const pendingIntro = useRef<'website' | 'github'>('website')
  const securitySeeded = useRef(false)
  // The ownership card's own state (minted nonce / verified / waived). Held here rather than inside the
  // card so it survives the card unmounting — the transcript re-renders constantly, and re-minting would
  // hand the user a second value after they had already published the first.
  const [securityNonceState, setSecurityNonceState] = useState<SecurityNonceState | null>(null)
  // Mirrors of state so runTurn can build the next turn's inputs without stale closures / a side
  // effect inside a setState updater (which StrictMode double-invokes in dev).
  const messagesRef = useRef<UIMessage[]>(messages)
  messagesRef.current = messages
  const configRef = useRef<OnboardingConfig>(config)
  configRef.current = config
  // Copy mirrored into a ref so callbacks read it without listing it as a dependency.
  const copyRef = useRef<OnboardingCopy>(copy)
  copyRef.current = copy

  // The sub-flow is complete once the user has either finished connecting GitHub or skipped it, and the
  // security question (which follows it) is no longer waiting for an answer. An unanswered ownership
  // CARD does not hold things up — see securityStage.
  const subflowDone = (githubStage === 'done' || githubStage === 'skipped') && securityStage !== 'offer'

  // Wait until authentication is resolved before probing so signed-out users can be shown the
  // sign-in CTA when a private app enters the reverse-proxy flow.
  const detectionReady = checked

  // Append a visible message to the transcript (used to narrate the initial app-check findings, the
  // login answer, and the GitHub-offer steps, so they show up in the chat and carry into the project's
  // chat history). Kept in messagesRef so the chat-phase seed can pick them up.
  const pushNarration = useCallback((role: 'user' | 'assistant', content: string) => {
    const msg = createMessage(role, content)
    const next = [...messagesRef.current, msg]
    messagesRef.current = next
    setMessages(next)
  }, [])

  // Clear the in-progress flag on the "Checking…" message and append the detection finding as its own
  // assistant turn, so the check stays as the first message in the transcript and the result follows it.
  const finalizeChecking = useCallback((content: string) => {
    const cleared = messagesRef.current.map((m) => (m.checking ? { ...m, checking: false } : m))
    const next = [...cleared, createMessage('assistant', content)]
    messagesRef.current = next
    setMessages(next)
  }, [])

  // Append the website intro (from the onboarding copy entry) after whatever's already in the
  // transcript. A leading hidden user turn keeps the wire history user-first for follow-ups. Priming
  // `ready` surfaces the Start QA button.
  const seedWebsiteIntro = useCallback(() => {
    const seeded: UIMessage[] = [
      { ...createMessage('user', `I've entered the URL ${targetUrl} and want to set up QA.`), hidden: true },
      ...messagesRef.current,
      createMessage('assistant', copyRef.current?.websiteIntro ?? ''),
    ]
    messagesRef.current = seeded
    setMessages(seeded)
    setReady(true)
  }, [targetUrl])

  // Append the GitHub-done intro after the connect + destination narration and prime Start QA.
  const seedGithubDoneIntro = useCallback(() => {
    const seeded: UIMessage[] = [
      { ...createMessage('user', `I've set up QA for ${targetUrl} and connected GitHub.`), hidden: true },
      ...messagesRef.current,
      createMessage('assistant', copyRef.current?.githubDoneIntro ?? ''),
    ]
    messagesRef.current = seeded
    setMessages(seeded)
    setReady(true)
  }, [targetUrl])

  // Seed whichever ready-intro the completed GitHub sub-flow earned.
  const seedReadyIntro = useCallback((kind: 'website' | 'github') => {
    if (kind === 'github') seedGithubDoneIntro()
    else seedWebsiteIntro()
  }, [seedWebsiteIntro, seedGithubDoneIntro])

  // The GitHub step is settled: ask about security testing before handing over to the ready intro.
  // Asked of everyone, because the point is that people know it exists — it was previously reachable
  // only by knowing to ask the agent for it, so nobody found it.
  //
  // Suppressed for a reverse-proxied app: ownership is proven by serving a file at a public URL, and
  // a tunnelled app has none. Those users get the offer from project settings instead.
  const proceedToSecurityOffer = useCallback((intro: 'website' | 'github') => {
    pendingIntro.current = intro
    if (configRef.current.useReverseProxy) {
      setSecurityStage('skipped')
      seedReadyIntro(intro)
      return
    }
    setSecurityStage('offer')
    if (!securitySeeded.current) {
      securitySeeded.current = true
      pushNarration('assistant', fill(copyRef.current?.securityOfferMessage ?? '', { url: targetUrl }))
    }
  }, [pushNarration, targetUrl, seedReadyIntro])

  // Record the Start QA button press in the transcript so the resulting project chat opens with a
  // clear "Start QA" turn — mirroring how every other chat button narrates the action it took.
  const recordStartInTranscript = useCallback(() => {
    const startTurn = createMessage('user', 'Start QA')
    const ack = createMessage('assistant', copyRef.current?.startAck ?? '')
    // Both turns are minted in the same synchronous tick, so createMessage stamps them with the same
    // millisecond createTime. The DB transcript is ordered `created_at ASC, id ASC`, so on that tie it
    // falls back to id — where the ack's "assistant-…" id sorts lexically before the user's "user-…" id
    // and flips the two. Stamp the ack a beat later so it always sorts after the user turn.
    ack.createTime = new Date(new Date(startTurn.createTime ?? Date.now()).getTime() + 1000).toISOString()
    const next = [...messagesRef.current, startTurn, ack]
    messagesRef.current = next
    setMessages(next)
  }, [])

  // Reset all per-URL state and return to the URL-entry screen (the "change URL" affordance).
  const resetToUrl = useCallback(() => {
    // Abandon any paused project + tunnel created for the previous URL (best-effort).
    if (proxyProject) {
      void deleteReverseProxy(proxyProject.id)
      void deleteProject(proxyProject.id)
    }
    setProxyProject(null)
    setGating(false)
    gatedCreated.current = false
    // Reset the GitHub sub-flow (no project created early, so nothing to tear down).
    setGithubStage('idle')
    setRepoUrl('')
    setGithubRepo(null)
    setGithubOptions(DEFAULT_GITHUB_TESTING_ANSWERS)
    startedFromGithub.current = false
    setAwaitingAppUrl(false)
    offerSeeded.current = false
    // A nonce is minted for one origin, so a new URL must start the security step over from scratch.
    setSecurityStage('idle')
    setSecurityNonceState(null)
    securitySeeded.current = false
    pendingIntro.current = 'website'
    setPhase('url')
    setMessages([])
    setReady(false)
    setComposerRevealed(false)
    setDetecting(false)
    setConfig({ ...DEFAULT_ONBOARDING_CONFIG })
    setLoginStepForm(false)
    setAwaitingLoginChoice(false)
    setPendingForm(null)
    setPendingChoices(null)
    setError(null)
    kickedOff.current = false
    detectStarted.current = false
  }, [proxyProject, deleteProject, deleteReverseProxy])

  // The app is ready (reachable/open, reverse-proxy narrated, or login answered): enter the GitHub
  // sub-flow inside the chat. Normally that's the Connect/Skip offer (narration seeded by the effect
  // below). A local app waits until its computer connection succeeds before automatically skipping
  // the optional GitHub step. When the user started from a GitHub repo URL we already know the repo,
  // so skip the offer entirely — narrate that we'll connect it and jump straight to the connect stage.
  const proceedToGithubOffer = useCallback((options: { waitForProxy?: boolean } = {}) => {
    setPhase('chat')
    // The login question (if it was asked) is answered now — clear it so the inline panel gives way.
    setAwaitingLoginChoice(false)
    setLoginStepForm(false)
    if (startedFromGithub.current) {
      if (!offerSeeded.current) {
        offerSeeded.current = true
        pushNarration('assistant', fill(copyRef.current?.githubStartConnect ?? '', { repo: githubRepoLabel(repoUrl) }))
      }
      setGithubStage('connect')
    } else if (options.waitForProxy) {
      setGithubStage('idle')
    } else {
      setGithubStage('offer')
    }
  }, [pushNarration, repoUrl])

  // Holds the latest chat-triggered start handler (assigned once the start functions below exist), so
  // runTurn's stream handlers can invoke it without depending on them directly.
  const triggerStartRef = useRef<() => void>(() => {})
  // Same indirection for the security card, which the agent can open (setup_security_pass) from a turn
  // started before the security handlers below are defined.
  const openSecurityCardRef = useRef<() => void>(() => {})

  // Run one agent turn: optionally append a user message, then stream the assistant's reply (and any
  // config/ready/form side-band events). `hidden` messages (the kickoff) are sent but not shown.
  const runTurn = useCallback(async (opts: { userText?: string; hidden?: boolean } = {}) => {
    setError(null)
    setPending(true)
    setPendingChoices(null)
    setAgentStatus(null)
    const base = opts.userText != null
      ? [...messagesRef.current, { ...createMessage('user', opts.userText), hidden: opts.hidden }]
      : messagesRef.current
    messagesRef.current = base
    setMessages(base)
    // Same client-side trim as the project chat. An onboarding transcript is short and carries no system
    // notices, so this is normally a no-op — it is here so both chats share one bound rather than one of
    // them quietly relying on the server to catch it.
    const full = toWireMessages(base)
    const capped = capTranscript(full)
    const turnCtx: ChatTurnContext = {
      mode: 'onboarding',
      projectId: null,
      turnCount: full.length,
      transcriptChars: full.reduce((n, m) => n + m.content.length, 0),
    }
    try {
      await streamChat(
        { mode: 'onboarding', targetUrl, timezone: currentChatTimezone(), isGithub: !!githubRepo, messages: capped.messages, trimmed: capped.trimmed, config: configRef.current },
        {
          onStall: (stats) => reportChatStall(stats, turnCtx),
          onStatus: (s) => setAgentStatus(s),
          onDelta: (id, text) => {
            setAgentStatus(null)
            setMessages((cur) => {
              const next = mergeMessage(cur, { id, role: 'assistant', content: text, createTime: new Date().toISOString() })
              messagesRef.current = next as UIMessage[]
              return next as UIMessage[]
            })
          },
          onConfig: (c) => { setConfig(c); configRef.current = c },
          onReady: () => setReady(true),
          onStartQa: () => triggerStartRef.current(),
          // The agent asking for security setup opens the SAME card the setup step uses, rather than a
          // form — one place owns the path and the value, so the two routes cannot drift apart.
          onForm: (form) => {
            if (form.kind === 'security-nonce') openSecurityCardRef.current()
            else setPendingForm(form)
          },
          onChoices: (choices) => setPendingChoices(choices),
        },
      )
    } catch (e) {
      // Report every dropped/empty onboarding turn (see chatTelemetry), and put the failure in the
      // transcript rather than leaving the user's message hanging with no reply. The onboarding
      // conversation is seeded into project_chat_messages on create, so the notice carries over.
      reportChatFailure(e, turnCtx)
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
      const notice = createMessage('assistant', chatFailureNotice(e))
      const next = [...messagesRef.current, notice] as UIMessage[]
      messagesRef.current = next
      setMessages(next)
    } finally {
      setAgentStatus(null)
      setPending(false)
    }
  }, [targetUrl, githubRepo])

  // Detect whether the entered website is publicly reachable and/or behind a login, then route to
  // the reverse-proxy / login step as needed. Best-effort: on failure we assume a public, open site
  // so onboarding is never blocked by a flaky probe.
  const runDetection = useCallback(async () => {
    setError(null)
    const c = copyRef.current
    if (!c) return
    // Seed the "Checking…" message if it isn't already present (a ?url= deep-link doesn't go through
    // submitUrl). It's the first message; finalizeChecking resolves it into the finding below.
    if (!messagesRef.current.some((m) => m.checking)) {
      const seeded = [...messagesRef.current, makeCheckingMessage(fill(c.checking, { url: targetUrl }))]
      messagesRef.current = seeded
      setMessages(seeded)
    }
    let f: WebsiteFeatures
    try {
      f = await detectWebsiteFeatures(targetUrl)
    } catch {
      // Probe failed: assume reachable so onboarding isn't blocked, but mark login 'unknown' so we
      // narrate honestly rather than falsely claiming the site is open.
      f = { url: targetUrl, publiclyAccessible: true, loginProtected: false, loginCertainty: 'unknown', reason: 'uncertain' }
    }
    if (!f.publiclyAccessible) {
      setConfig((prev) => {
        const next = { ...prev, useReverseProxy: true }
        configRef.current = next
        return next
      })
      // Narrate the reverse-proxy finding; the setup runbook renders below (ReverseProxyChatCard) and
      // Start QA stays disabled until the tunnel connects. For a normal URL flow, wait for that
      // connection before automatically skipping the optional GitHub step.
      finalizeChecking(fill(c.findingReverseProxy, { url: targetUrl }))
      proceedToGithubOffer({ waitForProxy: !startedFromGithub.current })
    } else if (f.loginProtected) {
      // Login confirmed: ask the login question inline in the transcript (below this finding message),
      // just like the GitHub offer. The GitHub offer is entered once the user answers.
      finalizeChecking(fill(c.findingLogin, { url: targetUrl }))
      setPhase('chat')
      setAwaitingLoginChoice(true)
    } else if (f.loginCertainty === 'unknown') {
      // Reachable, but we couldn't confirm whether it's open (a client-rendered SPA shell, a 5xx, or a
      // failed probe). Don't claim it's login-free — ask the same login question inline. It's skippable,
      // so it never blocks an open site, and the exploration login-pause still backstops a skipped wall.
      finalizeChecking(fill(c.findingReachableUncertain, { url: targetUrl }))
      setPhase('chat')
      setAwaitingLoginChoice(true)
    } else {
      // Reachable, open site: confirm the check passed, then move on to the GitHub offer.
      finalizeChecking(fill(c.findingReachable, { url: targetUrl }))
      proceedToGithubOffer()
    }
    setDetecting(false)
  }, [targetUrl, finalizeChecking, proceedToGithubOffer])

  // GitHub offer: once the sub-flow enters the 'offer' stage, seed the pitch narration once. The
  // Connect / Skip choices render below it (see the chat body). Local apps deliberately remain in
  // 'idle' until the computer connection succeeds, so they never show this optional interruption.
  useEffect(() => {
    if (githubStage !== 'offer' || offerSeeded.current) return
    offerSeeded.current = true
    pushNarration('assistant', copy.githubOfferMessage)
  }, [githubStage, pushNarration, copy])

  // A local app has already opted into the reverse-proxy path, so once its computer connection is
  // ready, complete the optional GitHub step automatically and reveal the normal Start QA state.
  // GitHub-first flows stay in the explicit connect stage because the user provided a repository.
  useEffect(() => {
    if (!config.useReverseProxy || !proxyReady || githubStage !== 'idle') return
    setGithubStage('skipped')
    seedWebsiteIntro()
  }, [config.useReverseProxy, proxyReady, githubStage, seedWebsiteIntro])


  // Run the reachability/login probe once the flow enters the chat shell AND auth is resolved. The
  // "Checking…" message is already seeded (submitUrl / initial state), so the shell renders populated
  // with no empty flash; runDetection resolves it into the finding.
  useEffect(() => {
    if (!detecting || detectStarted.current || !detectionReady) return
    detectStarted.current = true
    void runDetection()
  }, [detecting, runDetection, detectionReady])

  // Reverse-proxy flow: once the app needs a proxy, create the project up-front in a paused
  // ('awaiting_start') state so its tunnel can be provisioned + connected right here in the chat
  // before QA runs. Creation needs auth — the signed-out case shows a sign-in CTA instead. Runs once.
  useEffect(() => {
    if (phase !== 'chat' || !config.useReverseProxy) return
    if (!user || proxyProject || gatedCreated.current) return
    gatedCreated.current = true
    setGating(true)
    setError(null)
    void createProject({ ...buildCreatePayload(targetUrl, config), start_paused: true })
      .unwrap()
      .then((result) => {
        setProxyProject(result)
      })
      .catch((e) => {
        gatedCreated.current = false
        setError((e as { message?: string })?.message || 'Failed to prepare the local computer connection')
      })
      .finally(() => setGating(false))
  }, [phase, config, user, proxyProject, targetUrl, createProject])

  // The user submitted the inline login form (agent-requested, mid-chat): capture credentials into
  // the draft config (never into the chat transcript) and continue the conversation.
  const submitLogins = useCallback((result: { logins: { email: string; password: string }[]; autoCreateAccount: boolean }) => {
    const next = { ...configRef.current, logins: result.logins, autoCreateAccount: result.autoCreateAccount, loginChoice: loginChoiceFor(result.logins.length) }
    setConfig(next)
    configRef.current = next
    setPendingForm(null)
    const n = result.logins.length
    void runTurn({ userText: n > 0 ? `I've added ${n} login${n === 1 ? '' : 's'}.` : "I'll let agents self-register instead." })
  }, [runTurn])

  // Login step: user provided credentials in the form. Save them and continue, recording the answer
  // in the transcript (credentials themselves are never written to the chat — only the count).
  const completeLoginStep = useCallback((result: { logins: { email: string; password: string }[]; autoCreateAccount: boolean }) => {
    const next = { ...configRef.current, logins: result.logins, autoCreateAccount: result.autoCreateAccount, loginChoice: loginChoiceFor(result.logins.length) }
    setConfig(next)
    configRef.current = next
    const n = result.logins.length
    pushNarration('user', n > 0 ? `I've added ${n} test login${n === 1 ? '' : 's'}.` : "I'll let QA register its own test account.")
    proceedToGithubOffer()
  }, [pushNarration, proceedToGithubOffer])

  // Login step: user opted to let the agent register its own test account (no credentials).
  const chooseSelfSignup = useCallback(() => {
    const next: OnboardingConfig = { ...configRef.current, logins: [], autoCreateAccount: true, loginChoice: 'self-signup' }
    setConfig(next)
    configRef.current = next
    pushNarration('user', copyRef.current?.narrationSelfSignup ?? '')
    proceedToGithubOffer()
  }, [pushNarration, proceedToGithubOffer])

  // Login step: user opted to skip signed-in areas entirely (no credentials, no self-registration).
  const chooseSkipLogin = useCallback(() => {
    const next: OnboardingConfig = { ...configRef.current, logins: [], autoCreateAccount: false, loginChoice: 'skip' }
    setConfig(next)
    configRef.current = next
    pushNarration('user', copyRef.current?.narrationSkipLogin ?? '')
    proceedToGithubOffer()
  }, [pushNarration, proceedToGithubOffer])

  // A choice button was clicked on the login interstitial (initial website probe). "Provide login
  // credentials" reveals the add-logins form inline; the others complete the step.
  const handleLoginStepChoice = useCallback((choice: ChatChoice) => {
    if (choice.id === 'provide-logins') { setLoginStepForm(true); return }
    if (choice.id === 'create-account') { chooseSelfSignup(); return }
    chooseSkipLogin()
  }, [chooseSelfSignup, chooseSkipLogin])

  // A choice button was clicked in the setup chat (agent offered it via offer_choices).
  const handleChatChoice = useCallback((choice: ChatChoice) => {
    setPendingChoices(null)
    if (choice.action.kind === 'ui') {
      if (choice.action.ui === 'logins') setPendingForm({ kind: 'logins', title: 'Add a test login' })
      else if (choice.action.ui === 'security-nonce') openSecurityCardRef.current()
      return
    }
    void runTurn({ userText: choice.action.message })
  }, [runTurn])

  // The GitHub settings blob to persist at create, built from the connected repo + testing answers.
  // fileIssues is turned on when the user chose "GitHub Issues" as the bug destination (uses the App
  // installation token — no PAT needed). Returns undefined when no repo was connected (skipped).
  const githubSettingsForCreate = useCallback((): GithubSettings | undefined => {
    if (!githubRepo) return undefined
    const gh = buildGithubSettings(
      `https://github.com/${githubRepo.repoFullName}`,
      githubRepo.defaultBranch || 'main',
      githubOptions,
    )
    if (destinationUsesGithubIssues(bugDestination)) gh.fileIssues = true
    return gh
  }, [githubRepo, githubOptions, bugDestination])

  const issueTrackerForCreate = useCallback((): IssueTrackerCreatePayload | undefined => {
    return destinationIssueTracker(bugDestination)
  }, [bugDestination])

  // Snapshot the current GitHub sub-flow state for the sign-in round-trip.
  const pendingGithubState = useCallback((stage: GithubStage): PendingGithub => ({
    stage,
    repoUrl: repoUrl || undefined,
    repo: githubRepo ?? undefined,
    options: githubOptions,
    destination: bugDestination,
  }), [repoUrl, githubRepo, githubOptions, bugDestination])

  // About to leave for github.com to install the App. That is a same-tab round-trip — GitHub's Setup
  // URL brings the browser back to this screen — so snapshot the conversation the same way the sign-in
  // flows do, and the resume effect restores it on the way back. Without this the user comes back to an
  // empty new-project screen with everything they answered gone.
  const persistForGithubInstall = useCallback(() => {
    try {
      sessionStorage.setItem(PENDING_CHAT_PROJECT_KEY, JSON.stringify({
        targetUrl, config, messages: messagesRef.current, github: pendingGithubState('connect'),
      } satisfies PendingChatProject))
    } catch { /* ignore — a lost snapshot only costs the restore, never the install */ }
  }, [targetUrl, config, pendingGithubState])

  // ---- GitHub offer sub-flow handlers -----------------------------------------------------------

  const onOfferConnect = useCallback(() => {
    pushNarration('user', copyRef.current?.narrationConnect ?? '')
    const next = { ...configRef.current, environmentTrigger: 'github-app' as const }
    configRef.current = next
    setConfig(next)
    setGithubStage('repo')
  }, [pushNarration])

  const onOfferOnce = useCallback(() => {
    pushNarration('user', copyRef.current?.narrationRunOnce ?? copyRef.current?.narrationSkipGithub ?? '')
    const next = { ...configRef.current, environmentTrigger: 'manual' as const }
    configRef.current = next
    setConfig(next)
    setGithubStage('skipped')
    proceedToSecurityOffer('website')
  }, [pushNarration, proceedToSecurityOffer])

  const onOfferSchedule = useCallback(() => {
    pushNarration('user', copyRef.current?.narrationSchedule ?? '')
    setGithubStage('schedule')
  }, [pushNarration])

  const handleOfferChoice = useCallback((choice: ChatChoice) => {
    if (choice.id === 'connect') onOfferConnect()
    else if (choice.id === 'schedule') onOfferSchedule()
    else onOfferOnce()
  }, [onOfferConnect, onOfferOnce, onOfferSchedule])

  const handleScheduleChoice = useCallback((choice: ChatChoice) => {
    const environmentSchedule = choice.id === 'weekly' ? 'weekly' as const : 'nightly' as const
    const next = { ...configRef.current, environmentTrigger: 'schedule' as const, environmentSchedule }
    configRef.current = next
    setConfig(next)
    pushNarration('user', environmentSchedule === 'weekly' ? 'Run QA weekly on Sunday night.' : 'Run QA nightly.')
    setGithubStage('skipped')
    proceedToSecurityOffer('website')
  }, [pushNarration, proceedToSecurityOffer])

  const submitRepoUrl = useCallback((raw: string) => {
    if (!isGithubUrl(raw)) { setError('Enter a GitHub repository URL, e.g. github.com/owner/repo.'); return }
    setError(null)
    const normalized = normalizeUrl(raw)
    setRepoUrl(normalized)
    pushNarration('user', fill(copyRef.current?.narrationConnectRepo ?? '', { url: normalized }))
    setGithubStage('connect')
  }, [pushNarration])

  // The connect card reports the repo is connectable (App installed, no existing project) → record the
  // repo, auto-configure main-branch testing to detect deployments to the app URL (we don't ask the
  // testing questions), and finish. The "where should bugs go?" step is disabled for now (untested), so
  // we go straight to done; no external bug destination is configured. PR testing is left off too.
  // ---- Security-pass sub-flow handlers ----------------------------------------------------------

  // "Not now" — leave the pass off and move on. Nothing is written to the config, so a project created
  // from here is byte-for-byte what it would have been before this step existed.
  const onSecuritySkip = useCallback(() => {
    pushNarration('user', copyRef.current?.narrationSkipSecurity ?? '')
    pushNarration('assistant', copyRef.current?.securitySkipped ?? '')
    setSecurityStage('skipped')
    seedReadyIntro(pendingIntro.current)
  }, [pushNarration, seedReadyIntro])

  // "Yes" — turn the pass on in the draft config and show the ownership card. The intro is seeded now
  // rather than after verification, so Start QA is available while the user goes off to deploy a file.
  const onSecurityAccept = useCallback(() => {
    pushNarration('user', copyRef.current?.narrationSetupSecurity ?? '')
    pushNarration('assistant', copyRef.current?.securityStartMessage ?? '')
    const passes = configRef.current.enabledPolishPasses.includes('security')
      ? configRef.current.enabledPolishPasses
      : [...configRef.current.enabledPolishPasses, 'security']
    const next = { ...configRef.current, enabledPolishPasses: passes }
    configRef.current = next
    setConfig(next)
    setSecurityStage('setup')
    seedReadyIntro(pendingIntro.current)
  }, [pushNarration, seedReadyIntro])

  const handleSecurityChoice = useCallback((choice: ChatChoice) => {
    if (choice.id === 'setup-security') onSecurityAccept()
    else onSecuritySkip()
  }, [onSecurityAccept, onSecuritySkip])

  // The card reporting its state: a minted nonce, a verification result, or an account-level waiver.
  // Probing is enabled as soon as the nonce exists rather than only once verified, because the runner
  // re-checks ownership at the start of every run — an unverified project simply skips the pass, and
  // storing the nonce is what lets the user finish the setup later from project settings.
  const onSecurityNonceChange = useCallback((state: SecurityNonceState) => {
    setSecurityNonceState(state)
    const next = {
      ...configRef.current,
      securityProbingEnabled: true,
      securityNonce: state.waived ? '' : state.nonce,
      securityOwnershipExempt: state.waived,
    }
    configRef.current = next
    setConfig(next)
    if ((state.verified || state.waived) && securityStage === 'setup') {
      pushNarration('assistant', copyRef.current?.securityVerified ?? '')
      setSecurityStage('done')
    }
  }, [pushNarration, securityStage])

  // "Skip for now" on the card: keep the setup, stop showing it here. The pass stays inert until the
  // file goes live, which the user can confirm from project settings after their next deploy.
  const onSecurityDefer = useCallback(() => {
    pushNarration('assistant', copyRef.current?.securityDeferred ?? '')
    setSecurityStage('done')
  }, [pushNarration])

  // Open the card from somewhere other than the offer buttons — the agent's setup_security_pass tool, or
  // a choice it offered. Answering by voice instead of by button leaves the offer unanswered, so the
  // ready intro it was holding back has to be released here too or Start QA would never appear.
  const openSecurityCard = useCallback(() => {
    if (securityStage === 'offer') seedReadyIntro(pendingIntro.current)
    setSecurityStage('setup')
  }, [securityStage, seedReadyIntro])
  openSecurityCardRef.current = openSecurityCard

  const onGithubReady = useCallback((info: { repoFullName: string; defaultBranch: string }) => {
    const next = { ...configRef.current, environmentTrigger: 'github-app' as const }
    configRef.current = next
    setConfig(next)
    setGithubRepo(info)
    setGithubOptions({ mainBranchTesting: 'detect', mainBranchDeploymentUrl: targetUrl, prTesting: 'events' })
    pushNarration('assistant', fill(copyRef.current?.githubConnected ?? '', { repo: info.repoFullName, url: targetUrl }))
    setGithubStage('done')
    proceedToSecurityOffer('github')
  }, [pushNarration, targetUrl, proceedToSecurityOffer])

  // Keep a snapshot of a signed-out conversation so ANY trip through Auth0 comes back to it — not just
  // the three flows that persist explicitly (Start QA / reverse proxy / GitHub connect). Signing in from
  // the sidebar mid-setup used to save nothing, so the whole transcript was gone on return and the user
  // landed back on the empty "Set up QA" screen with everything they'd answered lost. Carries no resume
  // flags: the resume effect restores the transcript, config and sub-flow stage and stops there.
  useEffect(() => {
    if (user || explicitPersist.current) return
    if (phase !== 'chat' || messages.length === 0) return
    try {
      sessionStorage.setItem(PENDING_CHAT_PROJECT_KEY, JSON.stringify({
        targetUrl, config, messages, github: pendingGithubState(githubStage),
      } satisfies PendingChatProject))
    } catch { /* ignore — a lost snapshot only costs the restore, never the current session */ }
  }, [user, phase, messages, targetUrl, config, githubStage, pendingGithubState])

  // ---- Start QA ---------------------------------------------------------------------------------

  // Plain / GitHub-connected app (no reverse proxy). Create the project inline with the app URL plus
  // any github + issue_tracker settings, then navigate. Signed out, persist and create on resume.
  const startProject = useCallback(async () => {
    if (teamCannotPay) {
      setShowGate(true)
      return
    }
    setCreating(true)
    setError(null)
    recordStartInTranscript()
    if (!user) {
      try {
        sessionStorage.setItem(PENDING_CHAT_PROJECT_KEY, JSON.stringify({
          targetUrl, config, messages: messagesRef.current, start: true, github: pendingGithubState(githubStage),
        } satisfies PendingChatProject))
      } catch { /* ignore */ }
      explicitPersist.current = true
      login(dispatch, '/')
      return
    }
    try {
      const result = await createProject({
        ...buildCreatePayload(targetUrl, config),
        github: githubSettingsForCreate(),
        issue_tracker: issueTrackerForCreate(),
        chat_messages: chatMessagesForSeed(messagesRef.current),
      }).unwrap()
      requestSidebarExpand()
      try { sessionStorage.removeItem(PENDING_CHAT_PROJECT_KEY) } catch { /* ignore */ }
      navigate(`/projects/${result.id}/overview`)
    } catch (e) {
      setError((e as { message?: string })?.message || 'Failed to create project')
      setCreating(false)
    }
  }, [targetUrl, config, user, dispatch, navigate, recordStartInTranscript, githubSettingsForCreate, issueTrackerForCreate, githubStage, pendingGithubState, createProject, teamCannotPay])

  // Reverse-proxy flow: the user (signed out) asked to set up the proxy. Persist and sign in; the
  // resume effect recreates the paused project and stays on the onboarding chat.
  const signInForProxy = useCallback(() => {
    try {
      sessionStorage.setItem(PENDING_CHAT_PROJECT_KEY, JSON.stringify({
        targetUrl, config, messages: messagesRef.current, reverseProxy: true, github: pendingGithubState(githubStage),
      } satisfies PendingChatProject))
    } catch { /* ignore */ }
    explicitPersist.current = true
    login(dispatch, '/')
  }, [targetUrl, config, dispatch, githubStage, pendingGithubState])

  // Reverse-proxy flow: the tunnel is connected and the user pressed Start QA. Apply any github /
  // issue_tracker collected in the GitHub sub-flow onto the paused project, activate it, and launch QA.
  const startQaGated = useCallback(async () => {
    if (!proxyProject) return
    if (teamCannotPay) {
      setShowGate(true)
      return
    }
    setCreating(true)
    setError(null)
    recordStartInTranscript()
    const payload = buildCreatePayload(targetUrl, config)
    try {
      const gh = githubSettingsForCreate()
      const it = issueTrackerForCreate()
      if (gh || it) {
        await updateProject({
          id: proxyProject.id,
          data: {
            ...(gh ? { github: gh } : {}),
            ...(it ? { issue_tracker: it } : {}),
          },
        }).unwrap()
      }
      const result = await startQa({
        id: proxyProject.id,
        settings: { budget: payload.budget, design_document: payload.design_document, enabled_polish_passes: payload.enabled_polish_passes, testing_guidelines: payload.testing_guidelines ?? null },
        global_variables: payload.global_variables,
        instructions: payload.instructions,
        chat_messages: chatMessagesForSeed(messagesRef.current),
      }).unwrap()
      requestSidebarExpand()
      navigate(`/projects/${result.id}/overview`)
    } catch (e) {
      setError((e as { message?: string })?.message || 'Failed to start QA')
      setCreating(false)
    }
  }, [proxyProject, targetUrl, config, navigate, recordStartInTranscript, githubSettingsForCreate, issueTrackerForCreate, updateProject, startQa, teamCannotPay])

  // Signed-out from the connect card: persist enough to resume the connect walkthrough (no create).
  const signInForGithub = useCallback(() => {
    try {
      sessionStorage.setItem(PENDING_CHAT_PROJECT_KEY, JSON.stringify({
        targetUrl, config, messages: messagesRef.current,
        reverseProxy: config.useReverseProxy || undefined,
        github: pendingGithubState('connect'),
      } satisfies PendingChatProject))
    } catch { /* ignore */ }
    explicitPersist.current = true
    login(dispatch, '/')
  }, [targetUrl, config, dispatch, pendingGithubState])

  // The user clearly asked to start QA in the chat (agent called start_project). Kick the project off
  // exactly as the Start QA button would — same paths and gating. Only when the sub-flow is complete;
  // if starting isn't possible yet (signed out, or an unconnected reverse proxy) do nothing.
  const triggerStartFromChat = useCallback(() => {
    if (creating || !subflowDone) return
    if (config.useReverseProxy) {
      if (user && proxyProject && proxyReady) void startQaGated()
    } else {
      void startProject()
    }
  }, [creating, subflowDone, config.useReverseProxy, user, proxyProject, proxyReady, startQaGated, startProject])
  triggerStartRef.current = triggerStartFromChat

  // Resume a pending project after the sign-in round-trip.
  const resumed = useRef(false)
  useEffect(() => {
    if (!checked || !user || resumed.current) return
    let saved: PendingChatProject | null = null
    try {
      const raw = sessionStorage.getItem(PENDING_CHAT_PROJECT_KEY)
      if (raw) saved = JSON.parse(raw) as PendingChatProject
    } catch { /* ignore */ }
    if (!saved) return
    resumed.current = true
    const pendingMessages = saved.messages ?? []
    const g = saved.github

    // Restore the pre-sign-in transcript + config + sub-flow state.
    setTargetUrl(saved.targetUrl)
    setConfig(saved.config)
    configRef.current = saved.config
    setMessages(pendingMessages)
    messagesRef.current = pendingMessages
    setPhase('chat')
    kickedOff.current = true
    detectStarted.current = true
    offerSeeded.current = true
    if (g) {
      setGithubStage(g.stage)
      if (g.repoUrl) setRepoUrl(g.repoUrl)
      if (g.repo) setGithubRepo(g.repo)
      if (g.options) setGithubOptions(g.options)
    }
    const terminal = g?.stage === 'done' || g?.stage === 'skipped' || !g

    // Rebuild the github settings + issue_tracker from the persisted sub-flow state.
    const githubForCreate = (): GithubSettings | undefined => {
      if (!g?.repo) return undefined
      const gh = buildGithubSettings(`https://github.com/${g.repo.repoFullName}`, g.repo.defaultBranch || 'main', g.options ?? DEFAULT_GITHUB_TESTING_ANSWERS)
      if (destinationUsesGithubIssues(g.destination ?? null)) gh.fileIssues = true
      return gh
    }
    const issueTrackerForResume = destinationIssueTracker(g?.destination ?? null)
    if (g?.destination?.kind === 'email') void updateUserSettings({ notify_on_project_completion: true })

    // Start pressed while signed out (non-proxy): create the project now and navigate.
    if (saved.start && !saved.reverseProxy) {
      setReady(true)
      setCreating(true)
      void createProject({
        ...buildCreatePayload(saved.targetUrl, saved.config),
        github: githubForCreate(),
        issue_tracker: issueTrackerForResume,
        chat_messages: chatMessagesForSeed(pendingMessages),
      })
        .unwrap()
        .then((result) => {
          requestSidebarExpand()
          try { sessionStorage.removeItem(PENDING_CHAT_PROJECT_KEY) } catch { /* ignore */ }
          navigate(`/projects/${result.id}/overview`)
        })
        .catch((e) => {
          setError((e as { message?: string })?.message || 'Failed to create project')
          setCreating(false)
        })
      return
    }

    // Reverse-proxy resume: recreate the paused project and STAY on the onboarding chat so the user can
    // connect the tunnel + finish the sub-flow. Do not navigate.
    if (saved.reverseProxy || saved.config.useReverseProxy) {
      if (terminal) setReady(true)
      gatedCreated.current = true
      setGating(true)
      void createProject({ ...buildCreatePayload(saved.targetUrl, saved.config), start_paused: true })
        .unwrap()
        .then((result) => {
          setProxyProject(result)
          try { sessionStorage.removeItem(PENDING_CHAT_PROJECT_KEY) } catch { /* ignore */ }
        })
        .catch((e) => {
          gatedCreated.current = false
          setError((e as { message?: string })?.message || 'Failed to prepare the local computer connection')
        })
        .finally(() => setGating(false))
      return
    }

    // GitHub connect resume (non-proxy, mid-connect): restore to the connect stage; the card re-polls
    // now that we're signed in. Nothing to create yet.
    if (terminal) setReady(true)
    try { sessionStorage.removeItem(PENDING_CHAT_PROJECT_KEY) } catch { /* ignore */ }
  }, [checked, user, dispatch, navigate, createProject, updateUserSettings])

  // ---- Post-install landing (GitHub's App "Setup URL") -------------------------------------------

  // Installing the App leaves the app for github.com and comes back HERE, to the new-project screen,
  // with ?installation_id=… . The conversation itself is restored by the resume effect above: the
  // install round-trip is same-tab, so the snapshot written before leaving survives it exactly the way
  // it survives the Auth0 round-trip. These effects handle only the installation that just arrived.
  //
  // Every one of them is gated on `installationIdParam` because this component is also the plain
  // dashboard: consuming the return-path stash (or anything else single-use) on an ordinary visit to /
  // would eat a handoff the user is still in the middle of.

  // A project's GitHub tab stashes where it sent the user from, so land them back there rather than on
  // a generic new-project screen with no idea which project they were connecting.
  useEffect(() => {
    if (!installationIdParam) return
    const returnPath = takeGithubSetupReturnPath()
    if (returnPath) navigate(returnPath, { replace: true })
  }, [installationIdParam, navigate])

  // Signed out on arrival: stash the install params. The sign-in round-trip is supposed to preserve the
  // query string but can drop it, and without the id the installation can never be linked to the
  // account. We do NOT force a sign-in here — the user can keep setting up, and the effect below picks
  // the installation back up the moment they sign in.
  useEffect(() => {
    if (!installationIdParam || !checked || user) return
    saveGithubSetupParams(window.location.search)
  }, [installationIdParam, checked, user])

  // Signed in with no installation in the URL: restore a stash dropped by the sign-in round-trip. The
  // entry is single-use and TTL'd, and only the landing above ever writes one.
  useEffect(() => {
    if (!user || installationIdParam) return
    const saved = takeGithubSetupParams()
    if (!saved) return
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of new URLSearchParams(saved)) next.set(key, value)
    setSearchParams(next, { replace: true })
  }, [user, installationIdParam, searchParams, setSearchParams])

  // Read the installation. The GET is what CLAIMS it for this user, so this is the step that makes the
  // install show up under Settings → GitHub — it is not just for the account name below.
  const installationQuery = useGetGithubAppInstallationQuery(installationIdParam, {
    skip: !installationIdParam || !user,
  })
  // Latched, because stripping the params below unsubscribes the query and takes its data with it.
  const [installArrived, setInstallArrived] = useState(false)
  const [installedAccount, setInstalledAccount] = useState<string | null>(null)
  useEffect(() => {
    if (!installationQuery.data) return
    setInstallArrived(true)
    setInstalledAccount(installationQuery.data.accountLogin)
  }, [installationQuery.data])

  // Claim landed — drop the install params so a reload isn't a second arrival. Waiting for isSuccess
  // (rather than stripping on sight) keeps the query subscribed until the claim has actually happened.
  useEffect(() => {
    if (!installationIdParam || !installationQuery.isSuccess) return
    const next = new URLSearchParams(searchParams)
    next.delete('installation_id')
    next.delete('setup_action')
    next.delete('state')
    setSearchParams(next, { replace: true })
  }, [installationIdParam, installationQuery.isSuccess, searchParams, setSearchParams])

  // Whether to tell the user on the URL-entry screen that their install went through. Only on that
  // screen: reaching it means there was no conversation to resume (someone who installed straight from
  // github.com, with no setup in flight), whereas a resumed chat narrates the connection itself.
  const showInstalledNotice = installArrived && phase === 'url'

  // Drop into the chat shell with the given (normalized) app URL and run the reachability/login probe
  // inside it (detecting). Seed the "Checking…" message synchronously (copy is loaded by now) so the
  // shell renders populated. Shared by the plain and GitHub-first (awaitingAppUrl) entry paths.
  const beginDetection = useCallback((normalized: string) => {
    setError(null)
    setTargetUrl(normalized)
    // Append (not replace) so the GitHub-first app-URL Q&A already in the transcript is preserved; on
    // the plain path the transcript is empty here, so this is just the lone "Checking…" message.
    const checking = [...messagesRef.current, makeCheckingMessage(fill(copyRef.current?.checking ?? '', { url: normalized }))]
    messagesRef.current = checking
    setMessages(checking)
    setDetecting(true)
    setPhase('chat')
  }, [])

  // Enter the initial URL that kicks off setup. Same submit path from the composer's send button or
  // Enter. A GitHub repository URL is NOT rejected: we capture it and open the chat asking for the
  // deployed app URL (rather than a look-alike landing step), then connect the repo automatically at
  // the end.
  const submitUrl = useCallback((raw: string) => {
    // The composer is a prompt box, so a paste is often "https://app.example — a task app. Set up QA
    // for it." — extract the leading URL once and carry only that forward; the prose is for the
    // person reading it, not part of the target (see leadingUrl).
    const url = normalizeUrl(leadingUrl(raw))
    // Named event, not just the auto-instrumented click: FullStory only receives events trackEvent
    // forwards by name (clicks are excluded there to stay under its per-page custom-event cap), and the
    // signup funnel starts here. `outcome` separates the people who reached Auth0 from the ones a typo
    // stopped, which the click alone cannot tell apart. Fires for Enter as well as the button, since
    // both are the same submit.
    trackEvent('onboarding-start-clicked', {
      signedIn: !!user,
      outcome: isGithubUrl(url) ? 'github-repo' : urlLooksValid(url) ? 'app-url' : 'invalid-url',
    })

    // The gate. Validity is checked first so a typo is answered here, in place, rather than costing a
    // round trip to Auth0 and back to find out. The URL then rides along in returnTo, which is what
    // makes this a gate rather than a dead end: sign-in lands back on this screen with the app already
    // filled in, and the deep-link path above picks it straight up.
    if (!user) {
      if (isGithubUrl(url) || urlLooksValid(url)) {
        login(dispatch, `/new?url=${encodeURIComponent(url)}`)
      } else {
        setError('Enter a valid app URL to begin.')
      }
      return
    }
    if (isGithubUrl(url)) {
      setError(null)
      startedFromGithub.current = true
      const next = { ...configRef.current, environmentTrigger: 'github-app' as const }
      configRef.current = next
      setConfig(next)
      setRepoUrl(url)
      setAwaitingAppUrl(true)
      setPhase('chat')
      return
    }
    if (!urlLooksValid(url)) { setError('Enter a valid app URL to begin.'); return }
    setUrlDraft(raw)
    beginDetection(url)
  }, [beginDetection, user, dispatch])

  // GitHub-first branch: the user gave a repo URL, so now collect the deployed app URL to test (from
  // the composer in the chat). It must be the running app, not another GitHub URL. Record the answer in
  // the transcript, then resume the normal flow (detection appends to the same conversation).
  const submitAppUrl = useCallback((raw: string) => {
    const url = normalizeUrl(leadingUrl(raw))
    if (isGithubUrl(url)) { setError("Enter your app's public-facing URL — the running site to test, not another GitHub URL."); return }
    if (!urlLooksValid(url)) { setError('Enter a valid app URL to begin.'); return }
    setError(null)
    setAwaitingAppUrl(false)
    pushNarration('user', url)
    beginDetection(url)
  }, [beginDetection, pushNarration])

  if (phase === 'url') {
    // The initial experience mirrors nut.new's BaseChat landing: a centered hero over a prominent
    // prompt that reuses the same composer (ChatInput) the project chat column uses — here it takes
    // the app URL instead of a chat message.
    return (
      <div className="onboard onboard--landing" data-testid="onboarding-chat">
        <div className="onboard__intro">
          <div className="onboard__eyebrow"><Sparkles size={14} strokeWidth={1.75} aria-hidden /> New project</div>
          <h1 className="onboard__title" data-testid="onboard-title">{copy.title}</h1>
          <p className="onboard__lead">{copy.subtitle}</p>
          {/* Arrived straight from installing the App with no setup in flight: confirm the install
              landed (it is linked to the account now) and point at the one thing still missing. */}
          {showInstalledNotice && (
            <p className="onboard__lead" data-testid="onboard-github-installed">
              <Github size={14} strokeWidth={1.75} aria-hidden /> The Replay QA GitHub App is installed
              {installedAccount ? <> on <strong>{installedAccount}</strong></> : null}. Enter your app&apos;s
              URL to set up QA on it.
            </p>
          )}
        </div>
        <div className="onboard__prompt">
          {/* The button says where it goes. It still wants the URL first — an empty field disables it,
              and the URL is what comes back after sign-in — but jumping to Auth0 under a label that
              promised setup is the kind of surprise people read as a broken button. */}
          <ChatInput
            onSend={submitUrl}
            placeholder={copy.urlPlaceholder}
            submitLabel={user ? copy.startSetupLabel : copy.signedOutStartLabel}
            submitHint={<ArrowRight size={15} strokeWidth={2} aria-hidden />}
            initialValue={urlDraft}
            clearOnSend={false}
            autoFocus
            inline
          />
          {/* Floating (absolute) so a validation error doesn't shove the callouts/CTA below down. */}
          {error && <p className="onboard__error onboard__error--float" role="alert">{error}</p>}
        </div>
        {/* Let a coding agent drive QA. (The classic form also offers a "QA for free" callout; the
            chat onboarding intentionally omits it.) */}
        <div className="onboard__callouts">
          <AgentCta />
        </div>
      </div>
    )
  }

  const displayed = messages.filter((m) => !m.hidden)
  const lastDisplayed = displayed[displayed.length - 1]
  // While streaming, the assistant bubble fills in place — only show the typing indicator before the
  // first token arrives (i.e. while the last shown message is still the user's).
  const showTyping = pending && (!lastDisplayed || lastDisplayed.role === 'user')

  return (
    <div className="onboard onboard--chat" data-testid="onboarding-chat">
      <header className="onboard__chat-head">
        <div className="onboard__chat-title">
          <Sparkles size={16} strokeWidth={1.75} aria-hidden /> Set up QA
        </div>
        {/* Changing the URL starts setup over — it clears the transcript and everything answered so
            far, and deletes any proxy project already created — so it takes two clicks. The first turns
            this control into the confirm below rather than committing the reset. */}
        {!confirmReset ? (
          <button
            type="button"
            className="onboard__chat-url"
            onClick={() => setConfirmReset(true)}
            data-testid="onboard-change-url"
            data-analytics-event="onboard-change-url"
            title="Change URL"
          >
            {/* Before the app URL is given (GitHub-first), show the repo here; clicking still starts over. */}
            {awaitingAppUrl ? githubRepoLabel(repoUrl) : targetUrl} <Pencil size={12} aria-hidden />
          </button>
        ) : (
          <div className="onboard__reset-confirm" role="group" aria-label="Change URL" data-testid="onboard-change-url-confirm">
            <span className="onboard__reset-confirm-text">Start over? This clears your setup so far.</span>
            <button
              type="button"
              className="onboard__reset-confirm-btn onboard__reset-confirm-btn--go"
              onClick={() => { setConfirmReset(false); resetToUrl() }}
              data-testid="onboard-change-url-confirm-btn"
              data-analytics-event="onboard-change-url-confirm"
            >
              Start over
            </button>
            <button
              type="button"
              className="onboard__reset-confirm-btn"
              onClick={() => setConfirmReset(false)}
              data-testid="onboard-change-url-cancel-btn"
              data-analytics-event="onboard-change-url-cancel"
            >
              Cancel
            </button>
          </div>
        )}
      </header>

      <div className="onboard__chat-body">
        {/* GitHub-first: collect the deployed app URL right at the top of the chat — a clear title +
            one line + the composer, no icon or bubble — so it's obvious what to enter. Answering it
            resumes the normal flow (detection → the transcript takes over below). */}
        {awaitingAppUrl ? (
          <div className="onboard__appurl" data-testid="onboard-appurl-step">
            <h2 className="onboard__appurl-title">{copy.githubAppUrlTitle}</h2>
            <p className="onboard__appurl-text">{fill(copy.githubAppUrlSubtitle, { repo: githubRepoLabel(repoUrl) })}</p>
            <ChatInput
              onSend={submitAppUrl}
              placeholder={copy.urlPlaceholder}
              submitLabel={copy.startSetupLabel}
              submitHint={<ArrowRight size={15} strokeWidth={2} aria-hidden />}
              clearOnSend={false}
              autoFocus
              inline
            />
            {error && <p className="onboard__error" role="alert">{error}</p>}
          </div>
        ) : (
          <ChatMessageList
            items={displayed.map((m) => ({ id: m.id, message: m, anchor: m.role === 'user' }))}
            pending={pending}
            typing={showTyping}
            pendingLabel={agentStatus ?? 'Setting things up…'}
            emptyState={<div className="chat-empty">Starting the conversation…</div>}
          />
        )}

        {/* The login question, asked inline below the finding message — a confirmed login wall or an
            uncertain (SPA-shell) result. "Provide login credentials" reveals the add-logins form; the
            others (create test account / skip) answer and move on to the GitHub offer. */}
        {awaitingLoginChoice && (
          <div className="onboard__panel-wrap">
            {loginStepForm ? (
              <ChatLoginsForm
                title="Add a test login"
                initialLogins={config.logins}
                onSubmit={completeLoginStep}
                onCancel={() => setLoginStepForm(false)}
                onSelfSignup={chooseSelfSignup}
              />
            ) : (
              <ChatChoices choiceSet={loginChoiceSet()} onSelect={handleLoginStepChoice} disabled={pending || creating} />
            )}
          </div>
        )}

        {pendingForm?.kind === 'logins' && subflowDone && (
          <ChatLoginsForm
            title={pendingForm.title}
            initialLogins={config.logins}
            onSubmit={submitLogins}
            onCancel={() => setPendingForm(null)}
          />
        )}

        {pendingChoices && !pendingForm && subflowDone && (
          <ChatChoices choiceSet={pendingChoices} onSelect={handleChatChoice} disabled={pending || creating} />
        )}

        {/* Reverse-proxy setup, inline in the chat: a sign-in CTA when signed out, a spinner while the
            paused project is being created, then the connection runbook (which unlocks Start QA). */}
        {config.useReverseProxy && (
          <div className="onboard__panel-wrap">
            {!user ? (
              <div className="onboard-github" data-testid="onboard-proxy-signin">
                <p className="onboard-github__hint">
                  <Network size={14} strokeWidth={1.75} aria-hidden /> This app runs on your local computer. Sign in to connect it using the replayqa cli.
                </p>
                <button type="button" className="onboard-github__btn" onClick={signInForProxy} data-testid="onboard-proxy-signin-btn" data-analytics-event="onboard-proxy-signin">
                  Sign in to connect your local computer <ArrowRight size={14} aria-hidden />
                </button>
              </div>
            ) : gating || !proxyProject ? (
              <p className="onboard-github__hint" data-testid="onboard-proxy-provisioning">
                <Spinner size={14} className="onboard-spin" /> Preparing local computer connection…
              </p>
            ) : (
              <ReverseProxyChatCard project={proxyProject} autoStart={false} />
            )}
          </div>
        )}

        {/* GitHub-integration sub-flow, inline in the chat: offer → repo URL → connect → testing
            questions → bug destination. Once done (or skipped) it gives way to the intro + Start QA. */}
        {githubStage === 'offer' && (
          <div className="onboard__panel-wrap">
            <ChatChoices choiceSet={githubOfferChoiceSet(copy)} onSelect={handleOfferChoice} disabled={creating} />
          </div>
        )}
        {githubStage === 'schedule' && (
          <div className="onboard__panel-wrap">
            <ChatChoices choiceSet={scheduleChoiceSet(copy)} onSelect={handleScheduleChoice} disabled={creating} />
          </div>
        )}
        {githubStage === 'repo' && (
          <div className="onboard__panel-wrap">
            <div className="onboard__step-card" data-testid="onboard-github-repo">
              <div className="onboard__step-icon"><Github size={20} strokeWidth={1.75} aria-hidden /></div>
              <p className="onboard__step-text">{copy.githubRepoPrompt}</p>
              <ChatInput
                onSend={submitRepoUrl}
                placeholder="https://github.com/owner/repo"
                submitLabel={copy.githubRepoConnectLabel}
                submitHint={<ArrowRight size={15} strokeWidth={2} aria-hidden />}
                clearOnSend={false}
                autoFocus
                inline
              />
            </div>
          </div>
        )}
        {githubStage === 'connect' && (
          <div className="onboard__panel-wrap">
            <GithubConnectChatCard
              repoUrl={repoUrl}
              onReady={onGithubReady}
              onSignIn={signInForGithub}
              onBeforeInstall={persistForGithubInstall}
            />
          </div>
        )}

        {/* Security-pass sub-flow: the yes/no offer, then the ownership card. The card stays on screen
            beside the intro and Start QA — publishing the file usually needs a deploy, so this must not
            be a gate the user has to clear before anything else can happen. */}
        {securityStage === 'offer' && (
          <div className="onboard__panel-wrap">
            <ChatChoices choiceSet={securityChoiceSet(copy.securityOfferPrompt)} onSelect={handleSecurityChoice} disabled={creating} />
          </div>
        )}
        {/* Stays up after a successful check so the confirmation is visible where the instructions were,
            the way the reverse-proxy card does. Deferring ('done' with nothing confirmed) hides it. */}
        {(securityStage === 'setup' || (securityStage === 'done' && (securityNonceState?.verified === true || securityNonceState?.waived === true))) && (
          <div className="onboard__panel-wrap">
            <SecurityNonceChatCard
              targetUrl={targetUrl}
              value={securityNonceState}
              onChange={onSecurityNonceChange}
              onSkip={onSecurityDefer}
            />
          </div>
        )}

      </div>

      {ready && (
        <>
          {/* Once setup is ready, tuck the composer away behind a subtle reveal button so the input
              no longer looks like a required next step — Start QA is the prominent action. */}
          {!composerRevealed ? (
            <button
              type="button"
              className="onboard__composer-reveal"
              onClick={() => setComposerRevealed(true)}
              data-testid="onboard-composer-reveal"
              data-analytics-event="onboard-composer-reveal"
            >
              <MessageCircle size={15} strokeWidth={1.75} aria-hidden /> Need to tell me something else?
            </button>
          ) : (
            <ChatInput
              onSend={(text) => runTurn({ userText: text })}
              disabled={pending || creating || !!pendingForm}
              placeholder="Describe what to test, add logins, set a budget…"
              autoFocus
            />
          )}
          {/* Which team owns and pays for this project — asked here, at the moment of creation, because
              the answer decides whose credits the run spends and who keeps the results. It defaults to
              the user's own team, and is only rendered for someone in more than one; everyone else has
              no choice to make. This selection is scoped to project creation: it does not re-point the
              Team tab or Billing. */}
          {teams.length > 1 && (
            <div className="onboard__team">
              <label className="onboard__team-label" htmlFor="onboard-team">
                <Users size={15} strokeWidth={1.75} aria-hidden /> Select team
              </label>
              <div className="onboard__team-control">
                <select
                  id="onboard-team"
                  className="onboard__team-select"
                  value={projectTeam?.id ?? ''}
                  onChange={(e) => setProjectTeam(e.target.value || null)}
                  disabled={creating}
                  aria-describedby="onboard-team-hint"
                  data-testid="onboard-team"
                  data-analytics-event="onboard-team-select"
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                      {team.is_owner ? ' (yours)' : ''} — {Math.max(0, Math.floor(team.available_credits)).toLocaleString()} credits
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} strokeWidth={2} className="onboard__team-chevron" aria-hidden />
              </div>
              {/* Say what the choice does, since it is not recoverable from the control itself: the
                  team owns the project and pays for every run on it. When the chosen team has nothing
                  left, say that instead — pressing Start QA would only reach the upgrade screen. */}
              <p
                id="onboard-team-hint"
                className={`onboard__team-hint${teamCannotPay ? ' onboard__team-hint--warn' : ''}`}
                data-testid="onboard-team-hint"
              >
                {teamCannotPay
                  ? 'This team has no credits left. Choose a team with credits, or upgrade to run QA here.'
                  : 'This team owns the project, and its credits pay for every QA run on it.'}
              </p>
            </div>
          )}
          {config.useReverseProxy ? (
            // Reverse-proxy: only meaningful once signed in (else the sign-in CTA above leads the way).
            // Held disabled until the tunnel connects (proxyReady); pressing it activates the paused
            // project and launches QA.
            user ? (
              <>
                <button
                  type="button"
                  className="onboard__start-btn onboard__start-btn--below-input"
                  onClick={startQaGated}
                  disabled={creating || !proxyProject || !proxyReady}
                  data-testid="onboard-start-btn"
                  data-analytics-event="onboard-start-project"
                >
                  {creating ? <><Spinner size={16} className="onboard-spin" /> Starting…</> : <><Play size={16} strokeWidth={1.75} aria-hidden /> Start QA</>}
                </button>
                {!proxyReady && (
                  <p className="onboard__start-hint" data-testid="onboard-proxy-gate-hint">Connect your local computer above to enable Start QA.</p>
                )}
              </>
            ) : null
          ) : (
            <button
              type="button"
              className="onboard__start-btn onboard__start-btn--below-input"
              onClick={startProject}
              disabled={creating}
              data-testid="onboard-start-btn"
              data-analytics-event="onboard-start-project"
            >
              {creating ? <><Spinner size={16} className="onboard-spin" /> Starting…</> : <><Play size={16} strokeWidth={1.75} aria-hidden /> Start QA</>}
            </button>
          )}
          {/* Opting into security testing must never hold up the run: the file usually needs a deploy,
              and waiting for one is exactly the kind of dead end that made people abandon setup. Say
              plainly what starting now means instead of silently shipping a pass that won't fire. */}
          {securityStage === 'setup' && !securityNonceState?.verified && !securityNonceState?.waived && (
            <p className="onboard__start-hint" data-testid="onboard-security-pending-hint">
              QA will start without the security pass until that file is live. You can confirm it later in project settings.
            </p>
          )}
          {/* A randomized onboarding tip (`tips` in src/guidance/chat/onboarding.json), shown below the
              Start QA button while the project hasn't been created yet. */}
          {tip && (
            <p className="onboard__tip" data-testid="onboard-tip">
              <Lightbulb size={14} strokeWidth={1.75} aria-hidden />
              <span><strong>Tip:</strong> {tip}</span>
            </p>
          )}
        </>
      )}
      {error && <p className="onboard__error onboard__error--inline" role="alert">{error}</p>}
      {/* Dismissible, unlike the whole-surface gate: the way out of an unfunded team is often to pick
          a different one in the picker above, and closing puts the setup back exactly as it was. */}
      {showGate && <SubscriptionGate onClose={() => setShowGate(false)} />}
    </div>
  )
}
